"use client";
import React from 'react';
import Link from 'next/link';

import AdSlotWrapper from './AdSlotWrapper';
import RavzGadgetWidget from './RavzGadgetWidget';
import ShoppingWidget from './ShoppingWidget';

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hours ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return `1 day ago`;
  return `${diffInDays} days ago`;
};

const ArticleSidebar = ({ articles = [], settings = {} }) => {
  const siteSettings = settings;
  
  return (
    <aside className="w-full flex flex-col gap-10">
      
      {/* Ad Before Most Visited */}
      <AdSlotWrapper id="article_sidebar_1" style={{ minHeight: '600px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded"  settings={settings} />

      {/* Most Read List */}
      <div>
        <h3 className="text-2xl font-bold mb-6 border-b border-gray-300 pb-2">Most Visited</h3>
        <div className="flex flex-col gap-6">
          {(() => {
            const now = new Date();
            const fortyEightHoursAgo = new Date(now.getTime() - (48 * 60 * 60 * 1000));
            // Add fake views for sorting if they don't exist in DB
            const articlesWithViews = articles.map(a => {
               const idHash = (a.id || a.slug || "0").split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
               const fakeViews = (idHash * 331) % 50000 + 1000;
               return {
                 ...a,
                 _dateToUse: a.date || a.createdAt || a.publishedAt || "2024-01-01T00:00:00Z",
                 _viewsToUse: a.views || fakeViews
               };
            });

            let mostReadArticles = [...articlesWithViews]
              .filter(article => new Date(article._dateToUse) >= fortyEightHoursAgo)
              .sort((a, b) => b._viewsToUse - a._viewsToUse)
              .slice(0, 5);

            // Fallback to all-time most read if we have less than 5 articles in the last 48 hours
            if (mostReadArticles.length < 5) {
               const fallbackArticles = [...articlesWithViews]
                 .filter(article => !mostReadArticles.some(m => m.id === article.id))
                 .sort((a, b) => b._viewsToUse - a._viewsToUse)
                 .slice(0, 5 - mostReadArticles.length);
               mostReadArticles.push(...fallbackArticles);
            }

            return mostReadArticles.map((article, index) => (
              <Link key={article.id} href={article.slug === '#' ? '#' : `/${article.slug}`} className="group flex gap-5 items-center">
                <span className="text-5xl font-black text-brand-text w-8 flex-shrink-0 group-hover:text-brand transition-colors">{index + 1}</span>
                <div className="w-[100px] h-[85px] flex-shrink-0 bg-gray-200">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-base font-bold leading-snug group-hover:text-brand transition-colors line-clamp-2">
                    {article.title}
                  </h4>
                  <p className="text-[10px] text-brand-muted font-bold mt-1 uppercase tracking-wider" suppressHydrationWarning>
                    {formatTimeAgo(article._dateToUse)}
                    {siteSettings?.showMostReadViews === false ? null : ` • ${article._viewsToUse.toLocaleString()} Views`}
                  </p>
                </div>
              </Link>
            ));
          })()}
        </div>
      </div>

      {/* Ad After Most Visited */}
      <AdSlotWrapper id="article_sidebar_2" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded"  settings={settings} />

      <div className="mt-8 mb-6">
        <ShoppingWidget settings={settings} />
      </div>

      {/* Ad After Shopping */}
      <AdSlotWrapper id="article_sidebar_3" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded"  settings={settings} />

      <div className="mt-2 mb-6">
        <h3 className="text-2xl font-bold mb-4 uppercase text-brand-text border-b border-gray-300 pb-2">
          From Our Partners
        </h3>
        <AdSlotWrapper id="taboola_sidebar" className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" style={{ minHeight: '300px' }}  settings={settings} />
      </div>

      {/* Ad After Taboola */}
      <AdSlotWrapper id="article_sidebar_4" style={{ minHeight: '300px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded mt-4"  settings={settings} />

      {/* Dynamic Overflow Zone for Newly Generated Article Sidebar Slots */}
      {Object.keys(siteSettings?.ads?.slots || {})
        .filter(id => id.startsWith('article_sidebar_') && parseInt(id.replace('article_sidebar_', '')) > 3)
        .sort((a, b) => parseInt(a.replace('article_sidebar_', '')) - parseInt(b.replace('article_sidebar_', '')))
        .map(id => (
          <AdSlotWrapper key={id} id={id} style={{ minHeight: '300px' }} className="mt-4"  settings={settings} />
      ))}

    </aside>
  );
};

export default ArticleSidebar;
